import * as React from "react";

import { AlertCircle, Calendar, CheckCircle, Clock, Filter } from "lucide-react";
import { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import { ViewSnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/viewSnapshotEventDialog";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { useSearchParams } from "react-router";
import { SnapshotEventCard } from "@/modules/snapshots/snapshotEvents/components/SnapshotEventCard";
import { useSnapshotEventsContext } from "@/modules/snapshots/snapshotEvents/useSnapshotEventsContext";

interface Props {
  events: Array<GetSnapshotEventResponseItem>;
}

const DAY = 24 * 60 * 60 * 1000;

export const SnapshotEventsDashboard: React.FC<Props> = ({ events }) => {
  const [selectedFilter, setSelectedFilter] = React.useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterValue, setFilterValue] = React.useState(searchParams.get("documentName") || "");
  const eventIdParam = searchParams.get("eventId");

  const { selectedEvent, setSelectedEvent, selectedEventToEdit, setSelectedEventToEdit } = useSnapshotEventsContext();

  React.useEffect(() => {
    setSearchParams(prev => {
      prev.set("documentName", filterValue);
      return prev;
    });
  }, [filterValue, setSearchParams]);

  const eventsToDisplay = eventIdParam ? events.filter(ev => ev.id === eventIdParam) : events;

  const filteredEvents = eventsToDisplay.filter(ev => {
    if (selectedFilter === "all") return true;

    const isOverdue = ev.status === "in_progress" && +new Date(ev.event_date) + DAY < Date.now();

    if (selectedFilter === "upcoming") return ev.status === "in_progress" && !isOverdue;
    if (selectedFilter === "overdue") return ev.status === "in_progress" && isOverdue;
    if (selectedFilter === "complete") return ev.status === "done";
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        {/* Stats Overview - Four Separate Containers in One Row */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1 rounded-lg border bg-white p-4" style={{ borderColor: "#e9e0d5" }}>
            <div className="flex items-center justify-center gap-3">
              <Calendar size={24} style={{ color: "#d97757" }} />
              <div className="text-xl font-bold" style={{ color: "#0a213e" }}>
                {events.length}
              </div>
              <div className="text-xs" style={{ color: "#8b92af" }}>
                Total Events
              </div>
            </div>
          </div>

          <div className="flex-1 rounded-lg border bg-white p-4" style={{ borderColor: "#e9e0d5" }}>
            <div className="flex items-center justify-center gap-3">
              <AlertCircle size={24} className="text-danger" />
              <div className="text-xl font-bold" style={{ color: "#0a213e" }}>
                {
                  events.filter(
                    ({ status, event_date }) => status === "in_progress" && +new Date(event_date) + DAY < Date.now(),
                  ).length
                }
              </div>
              <div className="text-xs" style={{ color: "#8b92af" }}>
                Overdue
              </div>
            </div>
          </div>

          <div className="flex-1 rounded-lg border bg-white p-4" style={{ borderColor: "#e9e0d5" }}>
            <div className="flex items-center justify-center gap-3">
              <Clock size={24} style={{ color: "#8b92af" }} />
              <div className="text-xl font-bold" style={{ color: "#0a213e" }}>
                {
                  events.filter(
                    ({ status, event_date }) => status === "in_progress" && +new Date(event_date) + DAY >= Date.now(),
                  ).length
                }
              </div>
              <div className="text-xs" style={{ color: "#8b92af" }}>
                Upcoming
              </div>
            </div>
          </div>

          <div className="flex-1 rounded-lg border bg-white p-4" style={{ borderColor: "#e9e0d5" }}>
            <div className="flex items-center justify-center gap-3">
              <CheckCircle size={24} className="text-success" />
              <div className="text-xl font-bold" style={{ color: "#0a213e" }}>
                {events.filter(({ status }) => status === "done").length}
              </div>
              <div className="text-xs" style={{ color: "#8b92af" }}>
                Completed
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} style={{ color: "#8b92af" }} />
            <span className="text-sm font-medium" style={{ color: "#3c3c41" }}>
              Filter:
            </span>
          </div>
          {["all", "upcoming", "overdue", "complete"].map(filter => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              className="rounded-full px-2 py-1 text-sm font-medium capitalize transition-colors"
              style={{
                backgroundColor: selectedFilter === filter ? "#d97757" : "#ffffff",
                color: selectedFilter === filter ? "#ffffff" : "#3c3c41",
                border: `1px solid ${selectedFilter === filter ? "#d97757" : "#e9e0d5"}`,
              }}
            >
              {filter}
            </button>
          ))}

          <input
            type="text"
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            placeholder="Document name"
            className="w-96 max-w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {filteredEvents.map(event => (
          <SnapshotEventCard key={event.id} event={event} />
        ))}
      </div>
      {selectedEventToEdit && (
        <ModifySnapshotEventDialog
          open={selectedEventToEdit !== null}
          onClose={() => setSelectedEventToEdit(null)}
          snapshotEvent={selectedEventToEdit}
          businessReportOutcomeId={selectedEventToEdit.business_report_outcome_id as string}
          businessReportOutcomeResultId={selectedEventToEdit.business_report_outcome_result_id as string}
          mode="edit"
          eventType={selectedEventToEdit.event_type}
          eventDate={selectedEvent?.event_date ? new Date(selectedEventToEdit.event_date) : undefined}
        />
      )}
      {selectedEvent && (
        <ViewSnapshotEventDialog
          open={selectedEvent !== null}
          onClose={() => setSelectedEvent(null)}
          onEdit={ev => {
            setSelectedEvent(null);
            setSelectedEventToEdit(ev);
          }}
          snapshotEvent={selectedEvent}
        />
      )}
    </div>
  );
};
